import { Skeleton } from '@dixa/lib-shared-habla';
import { MetricInstrumentData } from 'lib-analytics-ui';
import { ampli } from 'lib-shared-tracking';
import { PropsWithClassName } from 'lib-shared-types-utils';
import moment from 'moment-timezone';
import { memo, useMemo } from 'react';

import { AppLink } from 'components/appLink';

import { useChannelDashboardChannelContext } from '../../../../channelDashboardChannel.context';
import { ChannelDashboardMetric } from '../../../metric/channelDashboardMetric';

type ChannelDashboardQueuedTimeMetricProps = PropsWithClassName;

export const ChannelDashboardQueuedTimeMetric = memo(
  function ChannelDashboardQueuedTimeMetric({
    className,
  }: ChannelDashboardQueuedTimeMetricProps): JSX.Element {
    const { metrics, isSkeleton } = useChannelDashboardChannelContext();

    /**
     * The displayed metric data is different based on whether the longest
     * queued time is longer than 1 day or not. If longer, we display a static
     * short duration, and if shorter we display a full live timer.
     */
    const metricData = useMemo((): JSX.Element => {
      const milliseconds = moment().diff(
        moment(metrics?.longest_queued_time),
        'milliseconds',
      );
      const isOverOneDay = milliseconds > 24 * 60 * 60 * 1000;

      const data = isOverOneDay ? (
        <MetricInstrumentData
          data={milliseconds}
          format="duration"
          suffix="+"
        />
      ) : (
        <MetricInstrumentData
          data={metrics?.longest_queued_time ?? null}
          format="timer"
        />
      );

      const longestQueuedCsid =
        metrics?.longest_queued_conversations?.[0]?.csid;

      if (longestQueuedCsid) {
        return (
          <AppLink
            path="/conversation/:csid"
            params={{ path: { csid: `${longestQueuedCsid}` } }}
            onClick={() => {
              ampli.dashboardChannelLongestQueuedTimeMetricClicked();
            }}
          >
            {data}
          </AppLink>
        );
      }
      return data;
    }, [metrics?.longest_queued_conversations, metrics?.longest_queued_time]);

    return (
      <ChannelDashboardMetric
        className={className}
        title="Longest queued time"
        isSkeleton={isSkeleton}
        metricType="queued_time"
      >
        {isSkeleton ? <Skeleton height="24px" width="72px" /> : metricData}
      </ChannelDashboardMetric>
    );
  },
);
